import { __awaiter } from "tslib";
import { action, makeObservable, observable } from "mobx";
import { parseCcDeviceLine, splitLines, withChecksum } from "./cc-device-protocol";
export const kMaxSeq = 999;
// How often an open session is pinged, and how long an unanswered open waits before it's re-sent.
export const kPingIntervalMs = 1000;
export const kOpenRetryMs = 2000;
// How many commands in a row can go unanswered before the device counts as not responding.
export const kNotRespondingAfter = 3;
// How long identify waits for the device's reply.
export const kIdentifyTimeoutMs = 2000;
function defaultRandomSessionId() {
    return 1 + Math.floor(Math.random() * 65535);
}
/**
 * The host side of one CC Device Protocol session: opens it, keeps it alive with pings,
 * recovers when the device forgets it, and routes readings to channels.
 */
export class CcDeviceSession {
    constructor(write, options = {}) {
        var _a;
        // Set when kNotRespondingAfter commands in a row have gone unanswered, or another host owns the
        // session. Cleared by the next reply to an open or ping, other than `othersession`.
        Object.defineProperty(this, "notResponding", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "write", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "randomSessionId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sessionId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "seq", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "opened", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "pending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        // Resolvers for identify commands still waiting, by #n. Kept apart from `pending` so stopping the
        // session doesn't drop them and their replies don't count as the session's.
        Object.defineProperty(this, "identifies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "buffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        // Opens and pings sent since the last reply to one. Not counted while the page is hidden: the
        // browser slows the transport's reads there, so replies fall behind the readings.
        Object.defineProperty(this, "unanswered", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "pingTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "openTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "handleVisibilityChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (document.visibilityState === "visible")
                    this.unanswered = 0;
            }
        });
        this.write = write;
        this.randomSessionId = (_a = options.randomSessionId) !== null && _a !== void 0 ? _a : defaultRandomSessionId;
        makeObservable(this, {
            notResponding: observable,
            setNotResponding: action
        });
    }
    setNotResponding(value) {
        this.notResponding = value;
    }
    start() {
        this.stop();
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
        this.openSession();
    }
    stop() {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        this.clearTimers();
        this.pending.clear();
        this.opened = false;
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            // An open still waiting for its ok may have reached the device. A close for a session the
            // device doesn't have changes nothing there.
            if (this.opened || this.openTimer) {
                const id = this.sessionId;
                this.stop();
                try {
                    yield this.write(withChecksum(`close session:${id}`));
                }
                catch (e) {
                    console.warn("CC Device session close failed", e);
                }
            }
            this.stop();
        });
    }
    receive(chunk, channels) {
        const { lines, remaining } = splitLines(this.buffer + chunk);
        this.buffer = remaining;
        lines.forEach(line => this.handleLine(line, channels));
    }
    sendOutput(name, value) {
        this.send(`${name}:${value}`);
    }
    // Asks the device to draw attention to itself. Any reply to it, ok or err, means it answered.
    identify() {
        const seq = this.nextSeq();
        return new Promise(resolve => {
            const timer = setTimeout(() => finish("noAnswer"), kIdentifyTimeoutMs);
            const finish = (result) => {
                clearTimeout(timer);
                this.identifies.delete(seq);
                resolve(result);
            };
            this.identifies.set(seq, finish);
            this.send(`identify #${seq}`);
        });
    }
    handleLine(line, channels) {
        const parsed = parseCcDeviceLine(line);
        if (!parsed)
            return;
        switch (parsed.kind) {
            case "reading":
                channels.filter(c => c.channelId === parsed.name).forEach(c => {
                    c.value = parsed.value;
                    c.lastMessageReceivedAt = Date.now();
                });
                break;
            case "ok":
                this.handleOk(parsed.seq);
                break;
            case "err":
                this.handleErr(parsed.seq, parsed.reason);
                break;
        }
    }
    answerIdentify(seq) {
        const finish = seq === undefined ? undefined : this.identifies.get(seq);
        finish === null || finish === void 0 ? void 0 : finish("answered");
        return !!finish;
    }
    handleOk(seq) {
        if (this.answerIdentify(seq))
            return;
        const kind = seq === undefined ? undefined : this.pending.get(seq);
        if (seq === undefined || !kind)
            return;
        this.pending.delete(seq);
        this.heardReply();
        if (kind === "open" && !this.opened) {
            this.opened = true;
            this.clearTimers();
            this.pingTimer = setInterval(() => this.ping(), kPingIntervalMs);
        }
    }
    handleErr(seq, reason) {
        if (this.answerIdentify(seq))
            return;
        const kind = seq === undefined ? undefined : this.pending.get(seq);
        // `nosession` and `othersession` change the session only when they answer a ping still
        // pending. Reopening clears the pending pings, so the errors for other pings in flight, or late
        // ones for an earlier session, change nothing.
        if ((reason === "nosession" || reason === "othersession") && kind !== "ping")
            return;
        // A reply to a pending command, other than `othersession`, shows the device is answering.
        if (seq !== undefined && kind) {
            this.pending.delete(seq);
            if (reason !== "othersession")
                this.heardReply();
        }
        if (reason === "nosession") {
            this.clearTimers();
            this.pending.clear();
            this.opened = false;
            this.openSession();
        }
        else if (reason === "othersession") {
            console.warn("CC Device: another host owns the session; giving up");
            this.stop();
            this.setNotResponding(true);
        }
        else {
            console.warn(`CC Device error${seq !== undefined ? ` #${seq}` : ""}: ${reason}`);
        }
    }
    openSession() {
        this.sessionId = this.randomSessionId();
        // The timer goes first, so an ok that arrives while the open is being written cancels it.
        this.openTimer = setInterval(() => this.sendOpen(), kOpenRetryMs);
        this.sendOpen();
    }
    sendOpen() {
        this.sendCommand(`open session:${this.sessionId}`, "open");
    }
    ping() {
        this.sendCommand(`ping session:${this.sessionId}`, "ping");
    }
    heardReply() {
        this.unanswered = 0;
        this.setNotResponding(false);
    }
    sendCommand(command, kind) {
        if (document.visibilityState !== "hidden") {
            if (this.unanswered >= kNotRespondingAfter)
                this.setNotResponding(true);
            this.unanswered++;
        }
        const seq = this.nextSeq();
        this.pending.set(seq, kind);
        this.send(`${command} #${seq}`);
    }
    nextSeq() {
        this.seq = this.seq >= kMaxSeq ? 1 : this.seq + 1;
        return this.seq;
    }
    clearTimers() {
        if (this.pingTimer)
            clearInterval(this.pingTimer);
        if (this.openTimer)
            clearInterval(this.openTimer);
        this.pingTimer = undefined;
        this.openTimer = undefined;
    }
    send(body) {
        try {
            const result = this.write(withChecksum(body));
            if (result)
                result.catch(e => console.warn("CC Device write failed", e));
        }
        catch (e) {
            console.warn("CC Device write failed", e);
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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